import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Badge,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import type {
  ContentSection,
  DocsGuardRefusal,
  PublishingQueueReason,
  PublishingQueueRow,
} from "@a7y/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { STATUS_BADGE_COLOR } from "./constants";
import {
  RefusalList,
  StatusControlDialog,
  type StatusControlTarget,
} from "./components/StatusControlDialog";
import { pickDiscardRevision } from "./publishing-actions";
import { buildStagingPreviewUrl } from "./utils";

const REASON_LABEL: Record<PublishingQueueReason, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  edits: "Unpublished edits",
  "status-change": "Status change queued",
  suggestion: "Agent suggestion",
};

/** A7Y-4480 — a row whose only reason is pending suggestions has nothing to publish yet: review them first. */
function onlySuggestions(row: PublishingQueueRow): boolean {
  return row.reasons.length === 1 && row.reasons[0] === "suggestion";
}

/** A7Y-4470: docs joins blog in the Publishing queue, each row branching on `row.section`. */
function rowEditorHref(row: PublishingQueueRow): string {
  return `/admin/content/${row.section}/${row.id}`;
}

/** `api.blog` and `api.docs` share the `contentSectionApi` factory surface (C5). */
function sectionApiFor(section: ContentSection) {
  return section === "docs" ? api.docs : api.blog;
}

/**
 * Renders a resolved-actor name, or — when the admin-directory lookup missed
 * (a deleted account, or no admin context, e.g. the scheduled-publish cron) —
 * the raw id muted with an "(unknown user)" hint, never presented as if it
 * were a real name (Karen round-1: "raw admin user id").
 */
function ActorLabel({ name, resolved }: { name: string; resolved: boolean }) {
  if (resolved) return <>{name}</>;
  return (
    <span className="text-zinc-400 dark:text-zinc-500">
      {name} <span className="text-xs italic">(unknown user)</span>
    </span>
  );
}

/**
 * Cross-section Publishing queue (A7Y-4474 D15): every draft, scheduled post,
 * published post with unpublished Firestore edits, and post with a queued
 * (not-yet-applied) status change, plus the 30-day status-change log.
 */
export function PublishingPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [statusDialogRow, setStatusDialogRow] = useState<{
    row: PublishingQueueRow;
    target: StatusControlTarget;
  } | null>(null);
  const [scheduleLocal, setScheduleLocal] = useState("");
  const [dialogPending, setDialogPending] = useState(false);
  // Docs only (A7Y-4470 D6/D5), mirroring ContentEditorPage's own state:
  // the row's current live slug (for the rename-redirect checkbox), the
  // redirect opt-out itself, and the refusals from the last blocked attempt.
  const [dialogLivePublishedSlug, setDialogLivePublishedSlug] = useState<string | null>(null);
  const [dialogRedirectFromPreviousSlug, setDialogRedirectFromPreviousSlug] = useState(true);
  const [dialogRefusals, setDialogRefusals] = useState<DocsGuardRefusal[]>([]);
  // Karen round 2 R2-F1: set when the `api.docs.get` fetch behind
  // `dialogLivePublishedSlug` fails, so the dialog discloses that the
  // redirect option is unavailable instead of it silently not appearing.
  const [dialogLiveSlugError, setDialogLiveSlugError] = useState<string | null>(null);
  // Karen round 2 R2-F3: the batch refusals from a mixed bulk publish's
  // `api.docs.publishBatch` 409, kept separate from `dialogRefusals` (the
  // per-row status dialog) since a bulk action has no single row to key on.
  const [bulkPublishRefusals, setBulkPublishRefusals] = useState<DocsGuardRefusal[]>([]);

  const queueQuery = useQuery({
    queryKey: ["content-publishing"],
    queryFn: () => api.content.publishing(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["content-publishing"] });

  const rows = queueQuery.data?.rows ?? [];
  const log = queueQuery.data?.log ?? [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  const toggleSelected = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePreview = async (row: PublishingQueueRow) => {
    setActionError(null);
    setBulkPublishRefusals([]);
    if (row.reasons.includes("edits")) {
      // A published post's draft preview 404s (blog.test.ts:469) — the only
      // way to see its pending edits is the in-admin editor preview.
      window.location.assign(rowEditorHref(row));
      return;
    }
    setPreviewingId(row.id);
    try {
      const res = await sectionApiFor(row.section).previewToken(row.id);
      if (res.ok) {
        window.open(
          buildStagingPreviewUrl(row.section, row.id, res.data.token),
          "_blank",
          "noopener,noreferrer"
        );
      } else {
        setActionError(res.error);
      }
    } finally {
      setPreviewingId(null);
    }
  };

  /**
   * "Discard edits" (A7Y-4474; generalised to docs at A7Y-4470): a
   * `status-change` row discards by clearing the queued status; an `edits`
   * row discards by restoring the newest `publish` revision — undoing every
   * Firestore edit made since the document last actually went live.
   */
  const handleDiscard = async (row: PublishingQueueRow) => {
    setActionError(null);
    setBulkPublishRefusals([]);
    if (row.reasons.includes("status-change")) {
      const res = await sectionApiFor(row.section).setPendingStatus(row.id, { status: null });
      if (res.ok) invalidate();
      else setActionError(res.error);
      return;
    }
    // The fetch is branched (not `sectionApiFor`) so each side keeps its own
    // concrete revision element type — a shared union type here would make
    // `.revisions` return `BlogRevision[] | DocPageRevision[]`, which
    // `pickDiscardRevision`'s generic can't infer a single `T` from.
    // `pickDiscardRevision` itself (generic over any `ContentRevisionBase`
    // shape) serves both branches unchanged.
    const target =
      row.section === "docs"
        ? pickDiscardRevision((await api.docs.revisions(row.id)).revisions)
        : pickDiscardRevision((await api.blog.revisions(row.id)).revisions);
    if (!target) {
      setActionError("No prior publish snapshot found to discard to.");
      return;
    }
    const res = await sectionApiFor(row.section).restoreRevision(row.id, target.id);
    if (res.ok) invalidate();
    else setActionError(res.error);
  };

  const openStatusDialog = async (row: PublishingQueueRow, target: StatusControlTarget) => {
    setScheduleLocal("");
    setActionError(null);
    setBulkPublishRefusals([]);
    setDialogRefusals([]);
    setDialogRedirectFromPreviousSlug(true);
    setDialogLivePublishedSlug(null);
    setDialogLiveSlugError(null);
    setStatusDialogRow({ row, target });
    if (row.section === "docs") {
      // The row projection carries only the current `slug`, not
      // `publishedSlug` — fetch the full document so the redirect checkbox
      // can compare against what's actually live, the same value
      // `ContentEditorPage` already has loaded via `serverDoc`. A failure
      // here must not silently drop the redirect option (Karen round 2
      // R2-F1) — it's disclosed in the dialog instead.
      try {
        const doc = await api.docs.get(row.id);
        setDialogLivePublishedSlug(doc.publishedSlug ?? null);
      } catch (e) {
        console.error("Failed to load the live page before publish", e);
        setDialogLiveSlugError(
          "Couldn't load the page's live URL, so the rename redirect option is unavailable.",
        );
      }
    }
  };

  const handleConfirmStatusDialog = async () => {
    if (!statusDialogRow) return;
    const { row, target } = statusDialogRow;
    const sectionApi = sectionApiFor(row.section);
    setDialogPending(true);
    setActionError(null);
    setBulkPublishRefusals([]);
    setDialogRefusals([]);
    try {
      const res =
        target === "published"
          ? await sectionApi.publish(
              row.id,
              row.section === "docs"
                ? { redirectFromPreviousSlug: dialogRedirectFromPreviousSlug }
                : undefined,
            )
          : target === "archived"
            ? await sectionApi.archive(row.id)
            : await sectionApi.schedule(row.id, new Date(scheduleLocal).toISOString());
      if (res.ok) {
        invalidate();
        setStatusDialogRow(null);
      } else {
        setActionError(res.error);
        if (res.refusals) setDialogRefusals(res.refusals);
      }
    } finally {
      setDialogPending(false);
    }
  };

  /**
   * A7Y-4470: a selected docs row batches through `api.docs.publishBatch`
   * (D7 — cross-linked docs pages validate against each other in one pass),
   * rather than looping `publish` one page at a time. Selected blog rows keep
   * the existing sequential, stop-on-first-failure loop.
   */
  const handleBulkPublish = async () => {
    setActionError(null);
    setBulkPublishRefusals([]);
    setBulkPublishing(true);
    try {
      const rowsById = new Map(rows.map((r) => [r.id, r]));
      const docIds: string[] = [];
      // Karen round 2 R2-F3: the blog loop and the docs batch are
      // independent actions on independent rows — a failure in one must not
      // silently erase a failure already recorded by the other.
      const errors: string[] = [];
      // Sequential, not Promise.all: each publish writes a KV read-model
      // record, and a burst of concurrent writes across an arbitrary batch
      // is exactly the load a bulk action must not throw at it.
      for (const id of selected) {
        if (rowsById.get(id)?.section === "docs") {
          docIds.push(id);
          continue;
        }
        const res = await api.blog.publish(id);
        if (!res.ok) {
          errors.push(res.error);
          break;
        }
      }
      if (docIds.length > 0) {
        const res = await api.docs.publishBatch(docIds);
        if (!res.ok) {
          errors.push(res.error);
          if (res.refusals) setBulkPublishRefusals(res.refusals);
        }
      }
      if (errors.length > 0) setActionError(errors.join(" · "));
      setSelected(new Set());
      invalidate();
    } finally {
      setBulkPublishing(false);
    }
  };

  return (
    <div className="px-4 py-0">
      <PageHeader title="Publishing" subtitle="Drafts, scheduled posts, and pending edits">
        <Link
          to="/admin/content/feedback"
          className="text-sm text-blue-600 hover:underline dark:text-blue-400"
        >
          Reader feedback
        </Link>
      </PageHeader>

      {actionError && (
        <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
          {actionError}
          {bulkPublishRefusals.length > 0 && <RefusalList refusals={bulkPublishRefusals} />}
        </div>
      )}

      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          {rows.length} item{rows.length === 1 ? "" : "s"} awaiting publication
        </p>
        <Button
          color="green"
          disabled={selected.size === 0 || bulkPublishing}
          onClick={() => void handleBulkPublish()}
        >
          {bulkPublishing ? "Publishing…" : `Publish selected (${selected.size})`}
        </Button>
      </div>

      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader>
              <Checkbox
                aria-label="Select all"
                checked={allSelected}
                onChange={(checked: boolean) =>
                  setSelected(checked ? new Set(rows.map((r) => r.id)) : new Set())
                }
              />
            </TableHeader>
            <TableHeader>Section</TableHeader>
            <TableHeader>Title</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Why it&rsquo;s here</TableHeader>
            <TableHeader>Changed</TableHeader>
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {queueQuery.isLoading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center">
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
              </TableCell>
            </TableRow>
          ) : queueQuery.isError ? (
            <TableRow>
              <TableCell colSpan={7} className="py-8 text-center text-red-600 dark:text-red-400">
                {queueQuery.error instanceof Error
                  ? queueQuery.error.message
                  : "Failed to load the publishing queue"}
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="py-8 text-center text-zinc-500">
                Nothing waiting on publication.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={`${row.section}:${row.id}`}>
                <TableCell>
                  <Checkbox
                    aria-label={`Select ${row.title}`}
                    checked={selected.has(row.id)}
                    onChange={() => toggleSelected(row.id)}
                  />
                </TableCell>
                <TableCell className="text-zinc-600 capitalize dark:text-zinc-400">
                  {row.section}
                </TableCell>
                <TableCell className="max-w-md font-medium whitespace-normal">
                  <Link
                    to={rowEditorHref(row)}
                    className="text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {row.title}
                  </Link>
                </TableCell>
                <TableCell>
                  <Badge color={STATUS_BADGE_COLOR[row.status] ?? "zinc"}>{row.status}</Badge>
                  {row.pendingStatus && (
                    <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                      queued → {row.pendingStatus.status}
                      {row.pendingStatus.goLiveAt &&
                        ` at ${formatDate(row.pendingStatus.goLiveAt)}`}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {row.reasons.map((r) => (
                      <Badge key={r} color={r === "suggestion" ? "indigo" : "zinc"}>
                        {r === "suggestion" && (row.suggestionCount ?? 0) > 1
                          ? `${row.suggestionCount} agent suggestions`
                          : REASON_LABEL[r]}
                      </Badge>
                    ))}
                  </div>
                  {row.agentTicketIds && row.agentTicketIds.length > 0 && (
                    <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
                      {row.agentTicketIds.map((t) => (
                        <a
                          key={t}
                          href={`https://linear.app/a7y/issue/${t}`}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline"
                        >
                          {t}
                        </a>
                      ))}
                      <Badge color={row.featureLive ? "emerald" : "zinc"}>
                        {row.featureLive ? "feature live" : "feature not live"}
                      </Badge>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-zinc-500">
                  <div>
                    <ActorLabel name={row.changedByName} resolved={row.changedByResolved} />
                  </div>
                  <div className="text-xs text-zinc-400">{formatDate(row.changedAt)}</div>
                </TableCell>
                <TableCell className="text-right">
                  {onlySuggestions(row) ? (
                    <div className="flex justify-end">
                      <Button outline href={rowEditorHref(row)}>
                        Review
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Button
                        plain
                        disabled={previewingId === row.id}
                        onClick={() => void handlePreview(row)}
                      >
                        {previewingId === row.id ? "Opening…" : "Preview"}
                      </Button>
                      <Button color="green" onClick={() => void openStatusDialog(row, "published")}>
                        Publish now
                      </Button>
                      <Button outline onClick={() => void openStatusDialog(row, "scheduled")}>
                        Schedule
                      </Button>
                      <Button color="red" onClick={() => void openStatusDialog(row, "archived")}>
                        Archive
                      </Button>
                      {(row.reasons.includes("edits") ||
                        row.reasons.includes("status-change")) && (
                        <Button plain onClick={() => void handleDiscard(row)}>
                          Discard
                        </Button>
                      )}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <h2 className="mt-8 mb-3 text-sm font-semibold text-zinc-900 dark:text-white">
        Status changes — last {queueQuery.data?.windowDays ?? 30} days
      </h2>
      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader>Title</TableHeader>
            <TableHeader>From</TableHeader>
            <TableHeader>To</TableHeader>
            <TableHeader>Actor</TableHeader>
            <TableHeader>When</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {log.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="py-6 text-center text-zinc-500">
                No status changes in this window.
              </TableCell>
            </TableRow>
          ) : (
            log.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="font-medium">{entry.title}</TableCell>
                <TableCell>
                  <Badge color={STATUS_BADGE_COLOR[entry.from] ?? "zinc"}>{entry.from}</Badge>
                </TableCell>
                <TableCell>
                  <Badge color={STATUS_BADGE_COLOR[entry.to] ?? "zinc"}>{entry.to}</Badge>
                </TableCell>
                <TableCell className="text-zinc-500">
                  <ActorLabel
                    name={entry.actorName ?? entry.actor}
                    resolved={entry.actorResolved ?? false}
                  />
                </TableCell>
                <TableCell className="text-zinc-500">{formatDate(entry.at)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <StatusControlDialog
        section={statusDialogRow?.row.section ?? "blog"}
        open={statusDialogRow !== null}
        target={statusDialogRow?.target ?? "published"}
        currentStatus={statusDialogRow?.row.status ?? "draft"}
        onClose={() => setStatusDialogRow(null)}
        onConfirm={() => void handleConfirmStatusDialog()}
        isPending={dialogPending}
        title={statusDialogRow?.row.title ?? ""}
        slug={statusDialogRow?.row.slug ?? ""}
        goLiveLocal={scheduleLocal}
        onGoLiveChange={setScheduleLocal}
        livePublishedSlug={dialogLivePublishedSlug}
        redirectFromPreviousSlug={dialogRedirectFromPreviousSlug}
        onRedirectFromPreviousSlugChange={setDialogRedirectFromPreviousSlug}
        refusals={dialogRefusals}
        liveSlugLoadError={dialogLiveSlugError}
      />
    </div>
  );
}

export default PublishingPage;
